import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Home,
  Calendar,
  LogOut,
  Receipt,
  Gift,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  User,
  Phone,
  MapPin,
  Upload,
  CreditCard,
  Copy,
  Building,
  AlertTriangle,
  Plus,
  ChevronRight,
  Shirt,
  Sparkles,
  Pencil,
  FileDown
} from 'lucide-react';
import RupiahIcon from '@/components/RupiahIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { getBillTotal, getUnpaidBills, getUnpaidTotal } from '@/lib/tenantBilling';
import TenantComplaintDialog from '@/components/TenantComplaintDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const hasRekeningDetails = (rekening) => ['rekening_bank', 'rekening_nomor', 'rekening_nama']
  .every(field => String(rekening?.[field] ?? '').trim().length > 0);
const emptyProfile = {
  rekening_bank: '', rekening_nomor: '', rekening_nama: '', has_second_tenant: false,
  penghuni2_nama: '', penghuni2_phone: '', penghuni2_status: '', penghuni2_ktp_url: '',
};

const TenantDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [bills, setBills] = useState([]);
  const [downloadingInvoice, setDownloadingInvoice] = useState('');
  const [billsError, setBillsError] = useState('');
  const [news, setNews] = useState([]);
  const [promos, setPromos] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  
  // Complaint modal
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [complaintForm, setComplaintForm] = useState({
    category: '',
    description: '',
    location_detail: '',
    preferred_date: '',
    preferred_time: '',
    file: null
  });
  
  // Checkout modal
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    requested_date: '',
    reason: '',
    rating_kebersihan: 0,
    rating_staff: 0,
    rating_keamanan: 0,
    testimoni: '',
    bank_name: '',
    bank_account: '',
    bank_holder: '',
  });
  
  // Rekening pengembalian deposit
  const [rekeningForm, setRekeningForm] = useState({ rekening_bank: '', rekening_nomor: '', rekening_nama: '' });
  const [rekeningLoaded, setRekeningLoaded] = useState(false);
  const [savingRekening, setSavingRekening] = useState(false);
  const [hasSavedRekening, setHasSavedRekening] = useState(false);
  const [rekeningError, setRekeningError] = useState('');
  const [profileForm, setProfileForm] = useState(emptyProfile);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [secondTenantKtp, setSecondTenantKtp] = useState(null);
  const [secondTenantKtpPreview, setSecondTenantKtpPreview] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [laundryRecords, setLaundryRecords] = useState([]);
  const [showLaundryModal, setShowLaundryModal] = useState(false);
  const [laundryForm, setLaundryForm] = useState({ pickup_date: '', pickup_time: '', jenis_barang: '', jenis_layanan: '', perkiraan_kg: '', notes: '' });
  const [submittingLaundry, setSubmittingLaundry] = useState(false);

  // Cleaning request
  const [cleaningRequests, setCleaningRequests] = useState([]);
  const [cleaningSettings, setCleaningSettings] = useState({});
  const [showCleaningModal, setShowCleaningModal] = useState(false);
  const [cleaningStep, setCleaningStep] = useState(1); // 1=form, 2=bayar+upload
  const [cleaningForm, setCleaningForm] = useState({ cleaning_date: '', cleaning_time: '' });
  const [cleaningActiveRequest, setCleaningActiveRequest] = useState(null);
  const [cleaningProofFile, setCleaningProofFile] = useState(null);
  const [uploadingCleaning, setUploadingCleaning] = useState(false);

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    proof_file: null,
    notes: ''
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('tenantToken');
    const tenantData = localStorage.getItem('tenant');
    
    if (!token || !tenantData) {
      navigate('/login');
      return;
    }
    
    fetchDashboard(token);
  }, [navigate]);

  const fetchDashboard = async (token) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const results = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/tenant/dashboard`, config),
        axios.get(`${BACKEND_URL}/api/tenant/payment-history`, config),
        axios.get(`${BACKEND_URL}/api/tenant/bills`, config),
        axios.get(`${BACKEND_URL}/api/tenant/news`, config),
        axios.get(`${BACKEND_URL}/api/tenant/promos`, config),
        axios.get(`${BACKEND_URL}/api/tenant/bank-accounts`, config),
        axios.get(`${BACKEND_URL}/api/tenant/complaints`, config),
        axios.get(`${BACKEND_URL}/api/tenant/rekening`, config),
        axios.get(`${BACKEND_URL}/api/tenant/cleaning-requests`, config).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/cleaning-settings`, config).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/tenant/laundry`, config).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/tenant/profile`, config).catch(() => ({ data: {} })),
      ]);

      if (results.some(result => result.status === 'rejected' && result.reason.response?.status === 401)) {
        handleLogout();
        return;
      }

      const [dashboardRes, paymentsRes, billsRes, newsRes, promosRes, bankRes, complaintsRes, rekeningRes, cleaningReqRes, cleaningSetRes, laundryRes, profileRes] = results
        .map(result => result.status === 'fulfilled' ? result.value : null);

      if (dashboardRes) setDashboard(dashboardRes.data);
      setPaymentHistory(paymentsRes?.data || []);
      if (Array.isArray(billsRes?.data)) {
        setBills(billsRes.data);
        setBillsError('');
      } else {
        setBills([]);
        setBillsError('Tagihan belum berhasil dimuat. Silakan coba lagi.');
      }
      setNews(newsRes?.data || []);
      setPromos(promosRes?.data || []);
      setBankAccounts(bankRes?.data || []);
      setComplaints(complaintsRes?.data || []);
      const rek = rekeningRes?.data || {};
      setRekeningForm({ rekening_bank: rek.rekening_bank || '', rekening_nomor: rek.rekening_nomor || '', rekening_nama: rek.rekening_nama || '' });
      setHasSavedRekening(hasRekeningDetails(rek));
      setRekeningLoaded(Boolean(rekeningRes));
      setCleaningRequests(cleaningReqRes?.data || []);
      setCleaningSettings(cleaningSetRes?.data || {});
      setLaundryRecords(laundryRes?.data || []);
      const profile = profileRes?.data && !Array.isArray(profileRes.data) ? profileRes.data : {};
      setProfileForm({
        ...emptyProfile,
        ...Object.fromEntries(Object.keys(emptyProfile).map(key => [key, profile[key] || ''])),
        has_second_tenant: Number(profile.jumlah_penghuni || 1) >= 2 || Boolean(profile.penghuni2_nama),
        rekening_bank: profile.rekening_bank || rek.rekening_bank || '',
        rekening_nomor: profile.rekening_nomor || rek.rekening_nomor || '',
        rekening_nama: profile.rekening_nama || rek.rekening_nama || '',
      });
      setSecondTenantKtpPreview(profile.penghuni2_ktp_url || '');
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRekening = async (event) => {
    event.preventDefault();
    setRekeningError('');
    if (!hasRekeningDetails(rekeningForm)) {
      setRekeningError('Lengkapi nama bank, nomor rekening, dan nama pemilik rekening.');
      return;
    }
    const rekening = Object.fromEntries(Object.entries(rekeningForm).map(([key, value]) => [key, value.trim()]));
    setSavingRekening(true);
    try {
      const token = localStorage.getItem('tenantToken');
      await axios.put(`${BACKEND_URL}/api/tenant/rekening`, rekening,
        { headers: { Authorization: `Bearer ${token}` } });
      setRekeningForm(rekening);
      setHasSavedRekening(true);
      setSuccess('Rekening berhasil disimpan!');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setRekeningError('Gagal menyimpan rekening. Silakan coba lagi.');
    } finally {
      setSavingRekening(false);
    }
  };

  const handleSecondTenantKtp = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setProfileError('Foto KTP penghuni kedua harus berupa file gambar.');
      event.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setProfileError('Ukuran foto KTP penghuni kedua maksimal 10 MB.');
      event.target.value = '';
      return;
    }
    setProfileError('');
    setSecondTenantKtp(file);
    const reader = new FileReader();
    reader.onload = () => setSecondTenantKtpPreview(String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    setProfileError('');
    const rekening = {
      rekening_bank: profileForm.rekening_bank.trim(),
      rekening_nomor: profileForm.rekening_nomor.trim(),
      rekening_nama: profileForm.rekening_nama.trim(),
    };
    if (Object.values(rekening).some(Boolean) && !Object.values(rekening).every(Boolean)) {
      setProfileError('Data rekening pengembalian deposit harus diisi lengkap.');
      return;
    }
    if (profileForm.has_second_tenant && ![profileForm.penghuni2_nama, profileForm.penghuni2_phone, profileForm.penghuni2_status].every(value => value.trim())) {
      setProfileError('Lengkapi nama, nomor HP, dan status penghuni kedua.');
      return;
    }
    if (profileForm.has_second_tenant && !secondTenantKtp && !profileForm.penghuni2_ktp_url) {
      setProfileError('Foto KTP penghuni kedua wajib diunggah.');
      return;
    }
    const fd = new FormData();
    Object.entries({
      ...rekening,
      has_second_tenant: profileForm.has_second_tenant ? 'true' : 'false',
      penghuni2_nama: profileForm.penghuni2_nama,
      penghuni2_phone: profileForm.penghuni2_phone,
      penghuni2_status: profileForm.penghuni2_status,
    }).forEach(([key, value]) => fd.append(key, value || ''));
    if (secondTenantKtp) fd.append('penghuni2_ktp', secondTenantKtp);
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('tenantToken');
      await axios.put(`${BACKEND_URL}/api/tenant/profile`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const nextRekening = { ...rekening };
      setRekeningForm(nextRekening);
      setHasSavedRekening(hasRekeningDetails(nextRekening));
      setProfileForm(current => ({
        ...current,
        ...nextRekening,
        penghuni2_nama: current.has_second_tenant ? current.penghuni2_nama : '',
        penghuni2_phone: current.has_second_tenant ? current.penghuni2_phone : '',
        penghuni2_status: current.has_second_tenant ? current.penghuni2_status : '',
        penghuni2_ktp_url: current.has_second_tenant ? (secondTenantKtpPreview || current.penghuni2_ktp_url) : '',
      }));
      if (!profileForm.has_second_tenant) setSecondTenantKtpPreview('');
      setSecondTenantKtp(null);
      setShowProfileModal(false);
      setSuccess('Rekening dan data penghuni kedua berhasil diperbarui!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (saveError) {
      setProfileError(saveError.response?.data?.detail || 'Gagal menyimpan perubahan. Silakan coba lagi.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tenantToken');
    localStorage.removeItem('tenant');
    navigate('/login');
  };

  const handleCleaningSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!cleaningForm.cleaning_date || !cleaningForm.cleaning_time) { setError('Isi tanggal dan waktu pembersihan'); return; }
    try {
      const token = localStorage.getItem('tenantToken');
      const { data } = await axios.post(`${BACKEND_URL}/api/tenant/cleaning-requests`, cleaningForm, { headers: { Authorization: `Bearer ${token}` } });
      setCleaningActiveRequest({ id: data.id, amount: data.amount });
      setCleaningStep(2);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal mengirim request'); }
  };

  const handleCleaningProofUpload = async () => {
    if (!cleaningProofFile) { setError('Pilih file bukti transfer'); return; }
    setUploadingCleaning(true);
    try {
      const token = localStorage.getItem('tenantToken');
      const fd = new FormData();
      fd.append('file', cleaningProofFile);
      await axios.put(`${BACKEND_URL}/api/tenant/cleaning-requests/${encodeURIComponent(cleaningActiveRequest.id)}/proof`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setSuccess('Bukti pembayaran berhasil diunggah! Admin akan memverifikasi.');
      setShowCleaningModal(false);
      setCleaningStep(1);
      setCleaningForm({ cleaning_date: '', cleaning_time: '' });
      setCleaningProofFile(null);
      setCleaningActiveRequest(null);
      fetchDashboard(token);
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal upload bukti'); }
    finally { setUploadingCleaning(false); }
  };

  const handleCheckoutRequest = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!checkoutForm.requested_date) {
      setError('Pilih tanggal checkout');
      return;
    }
    if (!checkoutForm.rating_kebersihan || !checkoutForm.rating_staff || !checkoutForm.rating_keamanan) {
      setError('Mohon lengkapi semua rating terlebih dahulu');
      return;
    }
    if (!checkoutForm.reason.trim()) {
      setError('Alasan keluar wajib diisi');
      return;
    }

    try {
      const token = localStorage.getItem('tenantToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(
        `${BACKEND_URL}/api/tenant/checkout-request`,
        checkoutForm,
        config
      );

      setSuccess('Permintaan checkout berhasil diajukan!');
      setShowCheckoutModal(false);
      fetchDashboard(token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal mengajukan checkout');
    }
  };

  const compressImage = (file) => new Promise((resolve, reject) => {
    const MAX_PX = 800;
    const QUALITY = 0.7;
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, MAX_PX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => blob ? resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })) : reject(new Error('Kompresi gagal')),
          'image/jpeg', QUALITY
        );
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!paymentForm.proof_file) {
      setError('Bukti foto transfer wajib diunggah sebelum pembayaran dikirim');
      return;
    }

    const MAX_MB = 10;
    if (paymentForm.proof_file.size > MAX_MB * 1024 * 1024) {
      setError(`Ukuran file terlalu besar (maks ${MAX_MB}MB). Pilih gambar yang lebih kecil.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('tenantToken');

      const compressed = await compressImage(paymentForm.proof_file);

      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('bill_id', selectedBill.created_at);
      if (paymentForm.notes) formData.append('notes', paymentForm.notes);

      const doUpload = () => axios.post(
        `${BACKEND_URL}/api/tenant/payment-proof`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
          timeout: 30000,
        }
      );

      try {
        await doUpload();
      } catch {
        // Server mungkin cold start — tunggu sebentar lalu coba lagi
        setUploadProgress(0);
        await new Promise(r => setTimeout(r, 2500));
        await doUpload();
      }

      setSuccess('Bukti pembayaran berhasil dikirim! Menunggu verifikasi admin.');
      setShowPaymentModal(false);
      setSelectedBill(null);
      setPaymentForm({ proof_file: null, notes: '' });
      setUploadProgress(0);
      fetchDashboard(token);
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg) setError(msg);
      else if (err.message === 'Kompresi gagal') setError('Gagal memproses gambar. Coba pilih gambar lain.');
      else if (!navigator.onLine) setError('Tidak ada koneksi internet. Periksa jaringan Anda.');
      else setError('Gagal mengirim bukti pembayaran. Coba lagi beberapa saat.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const openPaymentModal = (bill) => {
    setSelectedBill(bill);
    setShowPaymentModal(true);
    setError('');
  };

  const downloadInvoice = async (bill) => {
    const billId = String(bill.created_at);
    setDownloadingInvoice(billId);
    setError('');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tenant/bills/${encodeURIComponent(billId)}/invoice`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('tenantToken')}` }, responseType: 'blob',
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = match?.[1] || `Invoice-ISK-${bill.period || 'Tagihan'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(downloadError.response?.data?.detail || 'Invoice gagal diunduh. Silakan coba lagi.');
    } finally {
      setDownloadingInvoice('');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Nomor rekening disalin!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!complaintForm.category || !complaintForm.description) {
      setError('Pilih kategori dan isi deskripsi masalah');
      return;
    }
    
    try {
      const token = localStorage.getItem('tenantToken');
      const formData = new FormData();
      formData.append('property_name', dashboard?.property_name);
      formData.append('room_number', dashboard?.room_number);
      formData.append('category', complaintForm.category);
      formData.append('description', complaintForm.description);
      if (complaintForm.location_detail) formData.append('location_detail', complaintForm.location_detail);
      if (complaintForm.preferred_date) formData.append('preferred_date', complaintForm.preferred_date);
      if (complaintForm.preferred_time) formData.append('preferred_time', complaintForm.preferred_time);
      if (complaintForm.file) formData.append('file', complaintForm.file);
      
      await axios.post(`${BACKEND_URL}/api/complaints`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess('Komplain berhasil dikirim!');
      setShowComplaintModal(false);
      setComplaintForm({ category: '', description: '', location_detail: '', preferred_date: '', preferred_time: '', file: null });
      fetchDashboard(token);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal mengirim komplain');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      closed: 'bg-green-100 text-green-700'
    };
    const labels = {
      new: 'Baru',
      in_progress: 'Diproses',
      closed: 'Selesai'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.new}`}>{labels[status] || status}</span>;
  };

  const COMPLAINT_CATEGORIES = ['Listrik', 'Air', 'AC', 'Furniture', 'Kebersihan', 'Keamanan', 'Lainnya'];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const unpaidBills = getUnpaidBills(bills);
  const totalUnpaid = getUnpaidTotal(bills);

  if (loading) {
    return (
      <div className="min-h-screen bg-isk-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-isk-muted">Memuat dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-isk-bg">
      {/* Header - Fixed at top */}
      <div className="glass-nav border-b border-white/20 fixed top-0 left-0 right-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-isk-text">Halo, {dashboard?.tenant_name}!</h1>
              <p className="text-xs sm:text-sm text-isk-muted">{dashboard?.property_name} • Kamar {dashboard?.room_number}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { setProfileError(''); setShowProfileModal(true); }}
                variant="outline"
                size="sm"
                aria-label="Edit rekening dan penghuni kedua"
                className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
              >
                <Pencil className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Data Tenant</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                aria-label="Keluar"
                className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="max-w-4xl mx-auto px-4 pt-16 mt-3">
          <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
            {success}
          </div>
        </div>
      )}

      {/* Main Content - with padding for fixed header and bottom nav */}
      <div className="max-w-4xl mx-auto px-4 pt-16 pb-24">
        {billsError && (activeTab === 'home' || activeTab === 'bills') && (
          <div role="alert" className="my-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            <p>{billsError}</p>
            <Button variant="outline" className="mt-3" onClick={() => {
              setLoading(true);
              fetchDashboard(localStorage.getItem('tenantToken'));
            }}>Coba Lagi</Button>
          </div>
        )}
        {activeTab === 'home' && (
          <div className="space-y-6">
            {/* Rental Info Card */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-lg font-bold text-isk-text mb-4 flex items-center">
                <Home className="w-5 h-5 mr-2 text-isk-teal" />
                Detail Sewa
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-white/50">
                  <p className="text-xs text-isk-muted mb-1">Properti</p>
                  <p className="font-semibold text-isk-text">{dashboard?.property_name}</p>
                  <p className="text-xs text-isk-muted flex items-center mt-1">
                    <MapPin className="w-3 h-3 mr-1" />
                    {dashboard?.property_location || '-'}
                  </p>
                </div>
                
                <div className="p-4 rounded-xl bg-white/50">
                  <p className="text-xs text-isk-muted mb-1">Nomor Kamar</p>
                  <p className="font-semibold text-isk-text text-2xl">{dashboard?.room_number}</p>
                </div>
                
                <div className="p-4 rounded-xl bg-white/50">
                  <p className="text-xs text-isk-muted mb-1">Tanggal Check-in</p>
                  <p className="font-semibold text-isk-text flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-isk-teal" />
                    {formatDate(dashboard?.check_in_date)}
                  </p>
                </div>
                
                <div className="p-4 rounded-xl bg-white/50">
                  <p className="text-xs text-isk-muted mb-1">Harga Kamar</p>
                  <p className="font-semibold text-isk-teal flex items-center">
                    <RupiahIcon className="w-4 h-4 mr-2" />
                    {dashboard?.room_price ? formatCurrency(dashboard.room_price) : '-'}/bulan
                  </p>
                </div>
              </div>

              {/* Checkout Request Status */}
              {dashboard?.checkout_request ? (
                <div className={`mt-4 p-4 rounded-xl border ${dashboard.checkout_request.status === 'approved' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <p className={`text-sm flex items-center ${dashboard.checkout_request.status === 'approved' ? 'text-green-700' : 'text-yellow-700'}`}>
                    <Clock className="w-4 h-4 mr-2" />
                    {dashboard.checkout_request.status === 'approved'
                      ? `Checkout disetujui untuk tanggal ${formatDate(dashboard.checkout_request.scheduled_checkout_date || dashboard.checkout_request.requested_date)}`
                      : `Permintaan checkout menunggu persetujuan untuk tanggal ${formatDate(dashboard.checkout_request.requested_date)}`}
                  </p>
                </div>
              ) : (
                <Button
                  onClick={() => setShowCheckoutModal(true)}
                  variant="outline"
                  className="w-full mt-4 border-red-300 text-red-600 hover:bg-red-600 hover:text-white rounded-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Ajukan Checkout
                </Button>
              )}
            </div>

            {/* Unpaid Bills Alert */}
            {!billsError && (
              <div className="glass-card rounded-2xl p-6 border-2 border-red-200 bg-red-50/50">
                <h2 className="text-lg font-bold text-red-600 mb-3 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Tagihan Belum Lunas ({unpaidBills.length})
                </h2>
                <p className="text-sm text-isk-muted">Total Tagihan Belum Lunas</p>
                <p className="text-2xl font-bold text-red-600 mb-3">{formatCurrency(totalUnpaid)}</p>
                <div className="space-y-2">
                  {unpaidBills.slice(0, 3).map((bill, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-white/70">
                      <div>
                        <p className="font-medium text-isk-text">{bill.period || bill.description}</p>
                        <p className="text-xs text-isk-muted">Jatuh tempo: {formatDate(bill.due_date)}</p>
                      </div>
                      <p className="font-bold text-red-600">{formatCurrency(getBillTotal(bill))}</p>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-3" onClick={() => setActiveTab('bills')}>Lihat Semua Tagihan</Button>
              </div>
            )}

            {/* Pembersihan Kamar */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-isk-text flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Pembersihan Kamar
                  {cleaningRequests.filter(r => r.status === 'pending_payment').length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                      {cleaningRequests.filter(r => r.status === 'pending_payment').length} menunggu bayar
                    </span>
                  )}
                </h2>
                <Button size="sm" onClick={() => { setShowCleaningModal(true); setCleaningStep(1); setError(''); }}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full text-xs px-4">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Request
                </Button>
              </div>
              {cleaningRequests.length === 0 ? (
                <p className="text-sm text-isk-muted text-center py-4">Belum ada request pembersihan</p>
              ) : (
                <div className="space-y-2">
                  {cleaningRequests.slice(0, 5).map((cr, idx) => {
                    const statusMap = { pending_payment: { label: 'Belum Bayar', cls: 'bg-yellow-100 text-yellow-700' }, pending_verification: { label: 'Bukti Diunggah', cls: 'bg-blue-100 text-blue-700' }, confirmed: { label: 'Lunas', cls: 'bg-green-100 text-green-700' }, cancelled: { label: 'Dibatalkan', cls: 'bg-red-100 text-red-600' } };
                    const st = statusMap[cr.status] || statusMap.pending_payment;
                    const canPay = cr.status === 'pending_payment';
                    return (
                      <div key={idx}
                        className={`flex items-center justify-between gap-3 p-3 rounded-xl bg-white/50 ${canPay ? 'cursor-pointer hover:bg-white/70 border border-yellow-200' : ''}`}
                        onClick={canPay ? () => { setCleaningActiveRequest({ id: cr.id, amount: cr.amount }); setCleaningStep(2); setShowCleaningModal(true); setError(''); } : undefined}>
                        <div>
                          <p className="text-sm font-medium text-isk-text">
                            {new Date(cr.cleaning_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })} pukul {cr.cleaning_time}
                          </p>
                          <p className="text-xs text-isk-muted">{new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(cr.amount)}</p>
                          {canPay && <p className="text-xs text-yellow-700 font-medium mt-0.5">Tap untuk bayar</p>}
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Laundry */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-isk-text flex items-center gap-2">
                  <Shirt className="w-5 h-5 text-blue-500" />
                  Laundry
                </h2>
                <Button size="sm" onClick={() => { setShowLaundryModal(true); setLaundryForm({ pickup_date: '', pickup_time: '', jenis_barang: '', jenis_layanan: '', perkiraan_kg: '', notes: '' }); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs px-4">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Request Pickup
                </Button>
              </div>
              {laundryRecords.length === 0 ? (
                <p className="text-sm text-isk-muted text-center py-4">Belum ada riwayat laundry</p>
              ) : (
                <div className="space-y-2">
                  {laundryRecords.slice(0, 6).map((lr, idx) => {
                    const isSub = Number(lr.is_subscription) === 1;
                    const billOk = lr.bill_status === 'paid' || (isSub && lr.status === 'selesai');
                    const st = lr.status === 'ditolak'
                      ? { label: 'Ditolak', cls: 'bg-red-100 text-red-600' }
                      : lr.status === 'requested'
                      ? { label: 'Menunggu Konfirmasi', cls: 'bg-yellow-100 text-yellow-700' }
                      : lr.status === 'proses'
                      ? { label: isSub ? 'Diproses · Langganan' : 'Sedang Diproses', cls: 'bg-blue-100 text-blue-700' }
                      : billOk
                      ? { label: 'Selesai · Lunas', cls: 'bg-green-100 text-green-700' }
                      : lr.bill_total > 0
                      ? { label: 'Selesai · Belum Lunas', cls: 'bg-orange-100 text-orange-700' }
                      : { label: 'Selesai', cls: 'bg-green-100 text-green-700' };
                    return (
                      <div key={idx} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/50">
                        <div>
                          <p className="text-sm font-medium text-isk-text">
                            {lr.kg > 0 ? `${lr.kg} kg` : (lr.perkiraan_kg ? `~${lr.perkiraan_kg} kg` : 'Request Pickup')}
                          {lr.jenis_barang && <span className="ml-1 text-isk-muted font-normal">· {lr.jenis_barang}</span>}
                          <span className="ml-1 text-isk-muted font-normal">· {lr.pickup_date ? new Date(lr.pickup_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : new Date(lr.tanggal_masuk).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}{lr.pickup_time ? ` ${lr.pickup_time}` : ''}</span>
                          </p>
                          {lr.bill_total > 0 && <p className="text-xs text-isk-muted">{formatCurrency(lr.bill_total)}</p>}
                          {lr.notes && <p className="text-xs text-isk-muted italic">{lr.notes}</p>}
                        </div>
                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Promos */}
            {promos.length > 0 && (
              <div className="glass-card rounded-2xl p-6">
                <h2 className="text-lg font-bold text-isk-text mb-4 flex items-center">
                  <Gift className="w-5 h-5 mr-2 text-isk-gold" />
                  Promo Spesial
                </h2>
                <div className="space-y-4">
                  {promos.map((promo, idx) => (
                    <div key={idx} className="rounded-xl overflow-hidden border border-white/20">
                      {promo.image_url && (
                        <img 
                          src={promo.image_url} 
                          alt={promo.title}
                          className="w-full h-48 object-cover"
                        />
                      )}
                      <div className="p-4 bg-white/50">
                        <h3 className="font-bold text-isk-text">{promo.title}</h3>
                        {promo.description && (
                          <p className="text-sm text-isk-muted mt-1">{promo.description}</p>
                        )}
                        {promo.end_date && (
                          <p className="text-xs text-isk-gold mt-2">Berlaku sampai {formatDate(promo.end_date)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rekening Pengembalian Deposit — hanya tampil jika belum ada data */}
            {rekeningLoaded && !hasSavedRekening && (
              <div className="glass-card rounded-2xl p-6">
                <h2 className="text-lg font-bold text-isk-text mb-1 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-isk-teal" />
                  Rekening Pengembalian Deposit
                </h2>
                <p className="text-xs text-isk-muted mb-4">Isi rekening yang akan digunakan saat deposit dikembalikan</p>
                <form className="space-y-3" onSubmit={handleSaveRekening}>
                  <div>
                    <Label htmlFor="deposit-bank" className="text-xs text-isk-muted">Nama Bank</Label>
                    <select id="deposit-bank" required disabled={savingRekening} value={rekeningForm.rekening_bank} onChange={e => setRekeningForm(f => ({ ...f, rekening_bank: e.target.value }))}
                      className="mt-1 w-full glass-input border-2 focus:border-isk-teal rounded-xl px-3 py-2 text-sm text-isk-text focus:outline-none">
                      <option value="">-- Pilih bank --</option>
                      <option value="BCA">BCA</option>
                      <option value="GOPAY">GOPAY</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="deposit-number" className="text-xs text-isk-muted">Nomor Rekening / No. HP E-wallet</Label>
                    <Input id="deposit-number" required disabled={savingRekening} value={rekeningForm.rekening_nomor} onChange={e => setRekeningForm(f => ({ ...f, rekening_nomor: e.target.value }))}
                      placeholder="Nomor rekening atau nomor HP" className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="deposit-holder" className="text-xs text-isk-muted">Nama Pemilik Rekening</Label>
                    <Input id="deposit-holder" required disabled={savingRekening} value={rekeningForm.rekening_nama} onChange={e => setRekeningForm(f => ({ ...f, rekening_nama: e.target.value }))}
                      placeholder="Nama sesuai rekening" className="mt-1 rounded-xl" />
                  </div>
                  {rekeningError && <p role="alert" className="text-sm text-red-600">{rekeningError}</p>}
                  <Button
                    type="submit"
                    disabled={savingRekening}
                    className="w-full bg-isk-teal text-white rounded-full"
                  >
                    {savingRekening ? 'Menyimpan...' : 'Simpan Rekening'}
                  </Button>
                </form>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bills' && (
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-lg font-bold text-isk-text mb-4 flex items-center">
              <Receipt className="w-5 h-5 mr-2 text-isk-teal" />
              Tagihan
            </h2>
            
            {!billsError && (
              <div className="mb-4 p-4 rounded-xl bg-isk-teal/10">
                <p className="text-sm text-isk-muted">Total Tagihan Belum Lunas ({unpaidBills.length})</p>
                <p className="text-2xl font-bold text-isk-teal">{formatCurrency(totalUnpaid)}</p>
              </div>
            )}
            {billsError ? null : unpaidBills.length > 0 ? (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                {unpaidBills.map((bill, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl border-2 bg-white/50 border-red-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          Belum Lunas
                        </span>
                        {bill.bill_type === 'laundry' && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Laundry</span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-isk-text">{bill.period}</span>
                    </div>
                    
                    {/* Bill Details */}
                    <div className="space-y-2 mb-3">
                      {bill.rent_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">Sewa Kamar</span>
                          <span className="text-isk-text">{formatCurrency(bill.rent_amount)}</span>
                        </div>
                      )}
                      {bill.laundry_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">Laundry</span>
                          <span className="text-isk-text">{formatCurrency(bill.laundry_amount)}</span>
                        </div>
                      )}
                      {bill.parking_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">Parkir</span>
                          <span className="text-isk-text">{formatCurrency(bill.parking_amount)}</span>
                        </div>
                      )}
                      {bill.cleaning_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">Kebersihan</span>
                          <span className="text-isk-text">{formatCurrency(bill.cleaning_amount)}</span>
                        </div>
                      )}
                      {bill.other_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">{bill.other_description || 'Lain-lain'}</span>
                          <span className="text-isk-text">{formatCurrency(bill.other_amount)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="pt-2 border-t border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-isk-muted">Jatuh tempo: {formatDate(bill.due_date)}</p>
                        {bill.bill_type === 'laundry' && bill.status === 'unpaid' && (() => {
                          const daysLeft = Math.ceil((new Date(bill.due_date) - new Date()) / 86400000);
                          return daysLeft <= 3 && daysLeft >= 0
                            ? <p className="text-xs font-semibold text-red-600 mt-0.5">{daysLeft === 0 ? 'Jatuh tempo hari ini!' : `${daysLeft} hari lagi`}</p>
                            : null;
                        })()}
                      </div>
                      <p className="font-bold text-lg text-isk-teal">{formatCurrency(getBillTotal(bill))}</p>
                    </div>

                    <Button variant="outline" onClick={() => downloadInvoice(bill)} disabled={downloadingInvoice === String(bill.created_at)}
                      className="w-full mt-3 border-isk-teal/30 text-isk-teal rounded-full">
                      <FileDown className="w-4 h-4 mr-2" />
                      {downloadingInvoice === String(bill.created_at) ? 'Menyiapkan Invoice...' : 'Unduh Invoice PDF'}
                    </Button>

                    {/* Payment Button for unpaid bills */}
                    {bill.status === 'unpaid' && bill.payment_status === 'pending_verification' ? (
                      <div className="mt-3 p-2 rounded-lg bg-yellow-50 border border-yellow-200 text-center">
                        <p className="text-xs text-yellow-700 font-medium">Menunggu verifikasi admin</p>
                      </div>
                    ) : bill.status === 'unpaid' ? (
                      <Button
                        onClick={() => openPaymentModal(bill)}
                        className="w-full mt-3 bg-isk-teal hover:bg-isk-dark text-white rounded-full"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Bayar Sekarang
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-isk-muted">
                <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Tidak ada tagihan belum lunas</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bills' && (
          <div className="glass-card rounded-2xl p-6 mt-6">
            <h2 className="text-lg font-bold text-isk-text mb-4 flex items-center">
              <RupiahIcon className="w-5 h-5 mr-2 text-isk-teal" />
              Riwayat Pembayaran
            </h2>
            
            {paymentHistory.length > 0 ? (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                {paymentHistory.map((payment, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-green-50 border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        Lunas
                      </span>
                      <span className="text-sm font-medium text-isk-text">{payment.period}</span>
                    </div>
                    
                    {/* Payment Details */}
                    <div className="space-y-2 mb-3">
                      {payment.rent_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">Sewa Kamar</span>
                          <span className="text-isk-text">{formatCurrency(payment.rent_amount)}</span>
                        </div>
                      )}
                      {payment.laundry_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">Laundry</span>
                          <span className="text-isk-text">{formatCurrency(payment.laundry_amount)}</span>
                        </div>
                      )}
                      {payment.parking_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">Parkir</span>
                          <span className="text-isk-text">{formatCurrency(payment.parking_amount)}</span>
                        </div>
                      )}
                      {payment.other_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-isk-muted">{payment.other_description || 'Lain-lain'}</span>
                          <span className="text-isk-text">{formatCurrency(payment.other_amount)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="pt-2 border-t border-green-200 flex items-center justify-between">
                      <p className="text-xs text-green-600">Dibayar: {formatDate(payment.paid_at)}</p>
                      <p className="font-bold text-lg text-green-600">{formatCurrency(getBillTotal(payment))}</p>
                    </div>
                    <Button variant="outline" onClick={() => downloadInvoice(payment)} disabled={downloadingInvoice === String(payment.created_at)}
                      className="w-full mt-3 border-green-300 text-green-700 rounded-full">
                      <FileDown className="w-4 h-4 mr-2" />
                      {downloadingInvoice === String(payment.created_at) ? 'Menyiapkan Invoice...' : 'Unduh Invoice PDF'}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-isk-muted">
                <RupiahIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Belum ada riwayat pembayaran</p>
              </div>
            )}
          </div>
        )}


        {activeTab === 'promos' && (
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-lg font-bold text-isk-text mb-4 flex items-center">
              <Gift className="w-5 h-5 mr-2 text-isk-gold" />
              Promo Spesial
            </h2>
            
            {promos.length > 0 ? (
              <div className="space-y-4">
                {promos.map((promo, idx) => (
                  <div key={idx} className="rounded-xl overflow-hidden border border-white/20 bg-white/50">
                    {promo.image_url && (
                      <img 
                        src={promo.image_url} 
                        alt={promo.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="font-bold text-isk-text text-lg">{promo.title}</h3>
                      {promo.description && (
                        <p className="text-sm text-isk-muted mt-2">{promo.description}</p>
                      )}
                      {(promo.start_date || promo.end_date) && (
                        <p className="text-xs text-isk-gold mt-3 font-medium">
                          {promo.start_date && `Mulai: ${formatDate(promo.start_date)}`}
                          {promo.start_date && promo.end_date && ' - '}
                          {promo.end_date && `Sampai: ${formatDate(promo.end_date)}`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-isk-muted">
                <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Belum ada promo</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'complaints' && (
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-isk-text flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-orange-500" />
                Komplain
              </h2>
              {complaints.length > 0 && (
                <Button
                  onClick={() => setShowComplaintModal(true)}
                  size="sm"
                  className="bg-isk-teal hover:bg-isk-dark text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Buat
                </Button>
              )}
            </div>
            
            {complaints.length > 0 ? (
              <div className="space-y-3">
                {complaints.map((c, idx) => (
                  <div key={c.complaint_id || idx} role="button" tabIndex={0} aria-label={`Lihat detail ${c.complaint_id}`}
                    onClick={() => setSelectedComplaint(c)}
                    onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedComplaint(c); } }}
                    className="p-4 rounded-xl bg-white/50 border border-white/20 cursor-pointer hover:bg-white/80 focus:outline-none focus:ring-2 focus:ring-isk-teal">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-mono text-sm font-semibold text-isk-teal">{c.complaint_id}</span>
                        <p className="text-sm font-medium text-isk-text mt-1">{c.category}</p>
                      </div>
                      {getStatusBadge(c.status)}
                    </div>
                    <p className="text-sm text-isk-muted line-clamp-2">{c.description}</p>
                    <p className="text-xs font-medium text-isk-teal mt-2">Lihat detail komplain →</p>
                    {c.photo_url && (
                      <img 
                        src={`${BACKEND_URL}${c.photo_url}`} 
                        alt="Foto komplain" 
                        className="w-full h-32 object-cover rounded-lg mt-2"
                      />
                    )}
                    <p className="text-xs text-isk-muted mt-2">{formatDate(c.created_at)}</p>
                    {c.estimated_date && (
                      <p className="text-xs text-isk-teal mt-1">
                        Estimasi pengerjaan: {formatDate(c.estimated_date)} {c.estimated_time && `jam ${c.estimated_time}`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-isk-muted opacity-50" />
                <p className="text-isk-muted mb-4">Belum ada komplain</p>
                <Button
                  onClick={() => setShowComplaintModal(true)}
                  className="bg-isk-teal hover:bg-isk-dark text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Buat Komplain
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <TenantComplaintDialog complaint={selectedComplaint} onClose={() => setSelectedComplaint(null)} />

      {/* Bottom Navigation - Always fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-2 py-2 flex items-center justify-around">
          <button
            onClick={() => setActiveTab('home')}
            className={`flex flex-col items-center px-3 py-2 rounded-xl transition-all ${
              activeTab === 'home' ? 'text-isk-teal bg-isk-teal/10' : 'text-isk-muted'
            }`}
          >
            <img src="/isk-logo.png" alt="ISK" className="w-5 h-5 object-contain" />
            <span className="text-xs mt-1">Beranda</span>
          </button>
          
          <button
            onClick={() => setActiveTab('bills')}
            className={`flex flex-col items-center px-3 py-2 rounded-xl transition-all relative ${
              activeTab === 'bills' ? 'text-isk-teal bg-isk-teal/10' : 'text-isk-muted'
            }`}
          >
            <Receipt className="w-5 h-5" />
            <span className="text-xs mt-1">Tagihan</span>
            {unpaidBills.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unpaidBills.length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('promos')}
            className={`flex flex-col items-center px-3 py-2 rounded-xl transition-all ${
              activeTab === 'promos' ? 'text-isk-teal bg-isk-teal/10' : 'text-isk-muted'
            }`}
          >
            <Gift className="w-5 h-5" />
            <span className="text-xs mt-1">Promo</span>
          </button>
          
          <button
            onClick={() => setActiveTab('complaints')}
            className={`flex flex-col items-center px-3 py-2 rounded-xl transition-all relative ${
              activeTab === 'complaints' ? 'text-isk-teal bg-isk-teal/10' : 'text-isk-muted'
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
            <span className="text-xs mt-1">Komplain</span>
            {complaints.filter(c => c.status !== 'closed').length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                {complaints.filter(c => c.status !== 'closed').length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Modal rekening deposit dan penghuni kedua */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4 py-6" role="dialog" aria-modal="true" aria-label="Perbarui rekening dan penghuni kedua">
          <div className="glass-card rounded-2xl p-5 sm:p-6 max-w-xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-isk-text flex items-center gap-2"><User className="w-5 h-5 text-isk-teal" /> Data Tenant</h3>
                <p className="text-xs text-isk-muted mt-1">Kamar {dashboard?.room_number}</p>
              </div>
              <button type="button" aria-label="Tutup data tenant" onClick={() => setShowProfileModal(false)} className="text-isk-muted hover:text-isk-text"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-5">
              <section className="space-y-3">
                <h4 className="text-sm font-semibold text-isk-text flex items-center gap-2"><CreditCard className="w-4 h-4 text-isk-teal" /> Rekening Pengembalian Deposit</h4>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="profile-bank" className="text-xs">Nama Bank</Label>
                    <select id="profile-bank" value={profileForm.rekening_bank} onChange={e => setProfileForm(f => ({ ...f, rekening_bank: e.target.value }))} className="mt-1 w-full glass-input border-2 focus:border-isk-teal rounded-xl px-3 py-2 text-sm text-isk-text focus:outline-none">
                      <option value="">-- Pilih bank --</option><option value="BCA">BCA</option><option value="BRI">BRI</option><option value="BNI">BNI</option><option value="MANDIRI">Mandiri</option><option value="GOPAY">GoPay</option><option value="DANA">DANA</option>
                    </select>
                  </div>
                  <div><Label htmlFor="profile-account" className="text-xs">Nomor Rekening / HP</Label><Input id="profile-account" value={profileForm.rekening_nomor} onChange={e => setProfileForm(f => ({ ...f, rekening_nomor: e.target.value }))} className="mt-1 rounded-xl" /></div>
                  <div><Label htmlFor="profile-holder" className="text-xs">Nama Pemilik</Label><Input id="profile-holder" value={profileForm.rekening_nama} onChange={e => setProfileForm(f => ({ ...f, rekening_nama: e.target.value }))} className="mt-1 rounded-xl" /></div>
                </div>
              </section>

              <section className="space-y-3 border-t border-white/30 pt-4">
                <label className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/40 cursor-pointer">
                  <div>
                    <p className="text-sm font-semibold text-isk-text">Penghuni Kedua</p>
                    <p className="text-xs text-isk-muted">Aktifkan jika kamar ditempati dua orang</p>
                  </div>
                  <input id="has-second-tenant" type="checkbox" checked={profileForm.has_second_tenant} onChange={e => setProfileForm(f => ({ ...f, has_second_tenant: e.target.checked }))} className="w-5 h-5 accent-isk-teal" />
                </label>

                {profileForm.has_second_tenant && (
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div><Label htmlFor="second-tenant-name" className="text-xs">Nama Lengkap *</Label><Input id="second-tenant-name" required value={profileForm.penghuni2_nama} onChange={e => setProfileForm(f => ({ ...f, penghuni2_nama: e.target.value }))} className="mt-1 rounded-xl" /></div>
                      <div><Label htmlFor="second-tenant-phone" className="text-xs">Nomor HP *</Label><Input id="second-tenant-phone" required value={profileForm.penghuni2_phone} onChange={e => setProfileForm(f => ({ ...f, penghuni2_phone: e.target.value }))} className="mt-1 rounded-xl" /></div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="second-tenant-status" className="text-xs">Hubungan / Status *</Label>
                        <Input id="second-tenant-status" required value={profileForm.penghuni2_status} onChange={e => setProfileForm(f => ({ ...f, penghuni2_status: e.target.value }))} placeholder="Contoh: Suami, istri, saudara, teman" className="mt-1 rounded-xl" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Foto KTP Penghuni Kedua *</Label>
                      <div className="flex flex-col sm:flex-row gap-3 items-start mt-1">
                        {secondTenantKtpPreview ? <img src={secondTenantKtpPreview} alt="KTP penghuni kedua" className="w-full sm:w-48 h-28 object-cover rounded-xl border border-white/40" /> : <div className="w-full sm:w-48 h-28 rounded-xl bg-white/40 flex items-center justify-center text-xs text-isk-muted">Belum ada foto KTP</div>}
                        <label className="flex-1 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-isk-teal/30 text-sm text-isk-teal cursor-pointer hover:bg-isk-teal/5">
                          <Upload className="w-4 h-4" /> {secondTenantKtp ? secondTenantKtp.name : (secondTenantKtpPreview ? 'Ganti foto KTP' : 'Pilih foto KTP')}
                          <input type="file" accept="image/*" onChange={handleSecondTenantKtp} className="hidden" />
                        </label>
                      </div>
                      <p className="text-[11px] text-isk-muted mt-2">File gambar maksimal 10 MB.</p>
                    </div>
                  </div>
                )}
              </section>

              {profileError && <p role="alert" className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{profileError}</p>}
              <div className="flex gap-3 sticky bottom-0 bg-white/80 backdrop-blur py-3">
                <Button type="button" variant="outline" onClick={() => setShowProfileModal(false)} disabled={savingProfile} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={savingProfile} className="flex-1 bg-isk-teal text-white rounded-full">{savingProfile ? 'Menyimpan...' : 'Simpan Perubahan'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Laundry Request Pickup Modal */}
      {showLaundryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-isk-text flex items-center gap-2">
                <Shirt className="w-5 h-5 text-blue-500" /> Request Pickup Laundry
              </h3>
              <button onClick={() => setShowLaundryModal(false)} className="text-isk-muted hover:text-isk-text"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-isk-muted">Tanggal Pickup *</Label>
                  <Input type="date" value={laundryForm.pickup_date}
                    min={new Date().toISOString().slice(0,10)}
                    onChange={e => setLaundryForm(f => ({ ...f, pickup_date: e.target.value }))}
                    className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs text-isk-muted">Jam Pickup *</Label>
                  <select value={laundryForm.pickup_time}
                    onChange={e => setLaundryForm(f => ({ ...f, pickup_time: e.target.value }))}
                    className="mt-1 w-full glass-input border-2 focus:border-blue-400 rounded-xl px-3 py-2 text-sm text-isk-text focus:outline-none h-10">
                    <option value="">-- Pilih jam --</option>
                    {['09:00','13:00','18:00','22:00'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-isk-muted">Jenis Barang</Label>
                <Input value={laundryForm.jenis_barang}
                  onChange={e => setLaundryForm(f => ({ ...f, jenis_barang: e.target.value }))}
                  placeholder="Contoh: pakaian, seprai, handuk..."
                  className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-isk-muted">Jenis Layanan</Label>
                <select value={laundryForm.jenis_layanan}
                  onChange={e => setLaundryForm(f => ({ ...f, jenis_layanan: e.target.value }))}
                  className="mt-1 w-full glass-input border-2 focus:border-blue-400 rounded-xl px-3 py-2 text-sm text-isk-text focus:outline-none h-10">
                  <option value="">-- Pilih layanan --</option>
                  <option value="Express 8 Jam">Express 8 Jam</option>
                  <option value="Express 1 Hari">Express 1 Hari</option>
                  <option value="Reguler">Reguler</option>
                  <option value="Setrika">Setrika</option>
                  <option value="Cuci + Lipat">Cuci + Lipat</option>
                  <option value="Cuci + Setrika">Cuci + Setrika</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-isk-muted">Perkiraan Berat (kg)</Label>
                <Input type="number" step="0.5" min="0" value={laundryForm.perkiraan_kg}
                  onChange={e => setLaundryForm(f => ({ ...f, perkiraan_kg: e.target.value }))}
                  placeholder="Contoh: 3" className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-isk-muted">Catatan (opsional)</Label>
                <Textarea value={laundryForm.notes}
                  onChange={e => setLaundryForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Contoh: baju putih pisah, warna gelap pisah..."
                  className="mt-1 rounded-xl resize-none" rows={2} />
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={() => setShowLaundryModal(false)} variant="outline" className="flex-1 rounded-full">Batal</Button>
                <Button
                  onClick={async () => {
                    if (!laundryForm.pickup_date || !laundryForm.pickup_time) {
                      setError('Tanggal dan jam pickup wajib diisi');
                      setTimeout(() => setError(''), 3000);
                      return;
                    }
                    setSubmittingLaundry(true);
                    try {
                      const token = localStorage.getItem('tenantToken');
                      const cfg = { headers: { Authorization: `Bearer ${token}` } };
                      await axios.post(`${BACKEND_URL}/api/tenant/laundry/request`, laundryForm, cfg);
                      setSuccess('Request pickup berhasil dikirim!');
                      setTimeout(() => setSuccess(''), 3000);
                      setShowLaundryModal(false);
                      const r = await axios.get(`${BACKEND_URL}/api/tenant/laundry`, cfg);
                      setLaundryRecords(r.data || []);
                    } catch (e) {
                      setError(e.response?.data?.detail || 'Gagal mengirim request');
                      setTimeout(() => setError(''), 3000);
                    } finally { setSubmittingLaundry(false); }
                  }}
                  disabled={submittingLaundry}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                >
                  {submittingLaundry ? 'Mengirim...' : 'Kirim Request'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cleaning Request Modal */}
      {showCleaningModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-isk-text flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                {cleaningStep === 1 ? 'Request Pembersihan Kamar' : 'Pembayaran'}
              </h3>
              <button onClick={() => { setShowCleaningModal(false); setCleaningStep(1); setError(''); }} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

            {/* Step 1: Pilih tanggal & waktu */}
            {cleaningStep === 1 && (
              <form onSubmit={handleCleaningSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-isk-text">Tanggal Pembersihan</Label>
                  <input type="date" required value={cleaningForm.cleaning_date}
                    min={new Date().toISOString().slice(0,10)}
                    onChange={e => setCleaningForm(f => ({ ...f, cleaning_date: e.target.value }))}
                    className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-isk-text">Waktu Pembersihan</Label>
                  <input type="time" required value={cleaningForm.cleaning_time}
                    onChange={e => setCleaningForm(f => ({ ...f, cleaning_time: e.target.value }))}
                    className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                </div>
                {cleaningSettings.cleaning_price && (
                  <div className="p-3 rounded-xl bg-purple-50 border border-purple-100 text-sm text-isk-muted">
                    Biaya pembersihan: <span className="font-bold text-purple-700 text-base">
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(cleaningSettings.cleaning_price)}
                    </span>
                  </div>
                )}
                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full">
                  Lanjutkan
                </Button>
              </form>
            )}

            {/* Step 2: Info pembayaran + upload bukti */}
            {cleaningStep === 2 && cleaningActiveRequest && (
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-purple-50 border border-purple-100 text-center space-y-1">
                  <p className="text-sm text-isk-muted">Total yang harus dibayar</p>
                  <p className="text-3xl font-bold text-purple-700">
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(cleaningActiveRequest.amount)}
                  </p>
                </div>

                {(cleaningSettings.cleaning_bank || cleaningSettings.cleaning_rekening) && (
                  <div className="p-4 rounded-2xl bg-white border border-gray-200 space-y-2 text-sm">
                    <p className="font-semibold text-isk-text text-center mb-3">Transfer ke rekening berikut:</p>
                    {cleaningSettings.cleaning_bank && (
                      <div className="flex justify-between">
                        <span className="text-isk-muted">Bank</span>
                        <span className="font-semibold text-isk-text">{cleaningSettings.cleaning_bank}</span>
                      </div>
                    )}
                    {cleaningSettings.cleaning_rekening && (
                      <div className="flex justify-between items-center">
                        <span className="text-isk-muted">Nomor Rekening</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-isk-text text-base">{cleaningSettings.cleaning_rekening}</span>
                          <button onClick={() => { navigator.clipboard.writeText(cleaningSettings.cleaning_rekening); setSuccess('Nomor disalin!'); setTimeout(() => setSuccess(''), 2000); }}
                            className="text-isk-teal hover:text-isk-teal/70 text-xs border border-isk-teal/30 px-2 py-0.5 rounded-full">
                            Salin
                          </button>
                        </div>
                      </div>
                    )}
                    {cleaningSettings.cleaning_atas_nama && (
                      <div className="flex justify-between">
                        <span className="text-isk-muted">Atas Nama</span>
                        <span className="font-semibold text-isk-text">{cleaningSettings.cleaning_atas_nama}</span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium text-isk-text">Upload Bukti Transfer</Label>
                  <label className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-purple-200 rounded-xl p-6 cursor-pointer hover:border-purple-400 transition-colors bg-purple-50/30">
                    <Upload className="w-8 h-8 text-purple-400 mb-2" />
                    <span className="text-sm text-isk-muted">
                      {cleaningProofFile ? cleaningProofFile.name : 'Pilih foto bukti transfer'}
                    </span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => setCleaningProofFile(e.target.files[0])} />
                  </label>
                </div>

                <Button onClick={handleCleaningProofUpload} disabled={uploadingCleaning || !cleaningProofFile}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full">
                  {uploadingCleaning ? 'Mengunggah...' : 'Upload Bukti Pembayaran'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedBill && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Pembayaran Tagihan</h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Bill Summary */}
            <div className="p-4 rounded-xl bg-isk-teal/5 border border-isk-teal/20 mb-4">
              <p className="text-sm text-isk-muted mb-1">Tagihan Periode</p>
              <p className="font-bold text-isk-text text-lg">{selectedBill.period}</p>
              <p className="text-2xl font-bold text-isk-teal mt-2">
                {formatCurrency(getBillTotal(selectedBill))}
              </p>
            </div>

            {/* Bank Accounts */}
            <div className="mb-6">
              <p className="font-semibold text-isk-text mb-3 flex items-center">
                <Building className="w-4 h-4 mr-2" />
                Transfer ke Rekening:
              </p>
              <div className="space-y-3">
                {bankAccounts.map((bank, idx) => (
                  <div key={idx} className="p-3 rounded-xl bg-white/50 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-isk-text">{bank.bank_name}</p>
                        <p className="text-lg font-mono text-isk-teal">{bank.account_number}</p>
                        <p className="text-xs text-isk-muted">a.n. {bank.account_name}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(bank.account_number)}
                        className="border-isk-teal/30 text-isk-teal"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Transfer Notes Format */}
            {(() => {
              const monthMap = {
                'januari':'01','februari':'02','maret':'03','april':'04',
                'mei':'05','juni':'06','juli':'07','agustus':'08',
                'september':'09','oktober':'10','november':'11','desember':'12'
              };
              const periodParts = (selectedBill.period || '').split(' ');
              const mm = monthMap[(periodParts[0] || '').toLowerCase()] || String(new Date().getMonth() + 1).padStart(2, '0');
              const yy = periodParts[1] ? String(periodParts[1]).slice(-2) : String(new Date().getFullYear()).slice(-2);
              const room = dashboard?.room_number || '';
              const prop = dashboard?.property_name || '';
              const prefix = prop.toLowerCase().includes('kemayoran') ? 'IK' : 'IH';
              const transferNote = `${prefix}${yy}${mm}-${room}`;
              return (
                <div className="p-3 rounded-xl bg-isk-gold/10 border border-isk-gold/30">
                  <p className="text-xs font-semibold text-isk-text mb-1">Berita Transfer / Keterangan</p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-mono font-bold text-isk-teal text-lg tracking-wider">{transferNote}</p>
                    <Button type="button" size="sm" variant="outline"
                      className="border-isk-teal/30 text-isk-teal text-xs h-7"
                      onClick={() => copyToClipboard(transferNote)}>
                      Salin
                    </Button>
                  </div>
                  <p className="text-xs text-isk-muted mt-1">Wajib dicantumkan sebagai berita/keterangan saat transfer</p>
                </div>
              );
            })()}

            {/* Upload Form */}
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-isk-text font-semibold">
                  Upload Bukti Transfer *
                </Label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setPaymentForm({ ...paymentForm, proof_file: file });
                      }
                    }}
                    className="hidden"
                    id="proof-upload"
                  />
                  <label
                    htmlFor="proof-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-isk-teal/30 rounded-xl cursor-pointer hover:bg-isk-teal/5 transition-colors"
                  >
                    {paymentForm.proof_file ? (
                      <div className="text-center">
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-isk-text">{paymentForm.proof_file.name}</p>
                        <p className="text-xs text-isk-muted mt-1">Bukti foto wajib. Klik untuk ganti file</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-isk-teal mx-auto mb-2" />
                        <p className="text-sm text-isk-muted">Klik untuk pilih bukti foto transfer</p>
                        <p className="text-xs text-isk-muted mt-1">Wajib upload gambar JPG, PNG, atau WEBP</p>
                      </div>
                    )}
                  </label>
                </div>
                <p className="text-xs text-isk-muted">
                  Pembayaran hanya bisa dikirim jika bukti foto transfer atau screenshot pembayaran sudah diunggah.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-isk-text font-semibold">
                  Berita Transfer / Catatan
                </Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Isi dengan kode berita transfer di atas"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  rows={2}
                />
              </div>

              <div className="flex space-x-3 pt-2">
                <Button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                  disabled={uploading}
                >
                  Batal
                </Button>
                {uploading && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-isk-teal h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                    <p className="text-xs text-isk-muted text-center mt-1">
                      {uploadProgress < 100 ? `Mengunggah... ${uploadProgress}%` : 'Memproses...'}
                    </p>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={uploading || !paymentForm.proof_file}
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Mengirim...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Kirim Bukti
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Ajukan Checkout</h3>
              <button onClick={() => setShowCheckoutModal(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleCheckoutRequest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="checkout_date">Tanggal Checkout *</Label>
                <Input
                  id="checkout_date"
                  type="date"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  value={checkoutForm.requested_date}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, requested_date: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                />
                {checkoutForm.requested_date && (
                  <p className="text-xs text-isk-muted flex items-center gap-1">
                    <span>Checkout akan diproses pada tanggal yang dipilih setelah disetujui Finance.</span>
                  </p>
                )}
              </div>

              {/* Survey Rating */}
              <div className="space-y-3 p-4 rounded-xl bg-white/40 border border-white/30">
                <p className="text-sm font-semibold text-isk-text">Survey Kepuasan</p>
                {[
                  { key: 'rating_kebersihan', label: 'Kebersihan' },
                  { key: 'rating_staff', label: 'Kinerja Staff' },
                  { key: 'rating_keamanan', label: 'Keamanan' },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs text-isk-muted">{label} *</Label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setCheckoutForm({ ...checkoutForm, [key]: star })}
                          className={`w-10 h-10 rounded-xl text-lg font-bold transition-all border-2 ${
                            checkoutForm[key] >= star
                              ? 'bg-isk-gold border-isk-gold text-white'
                              : 'bg-white/60 border-gray-200 text-gray-300 hover:border-isk-gold hover:text-isk-gold'
                          }`}
                        >
                          ★
                        </button>
                      ))}
                      {checkoutForm[key] > 0 && (
                        <span className="text-xs text-isk-muted self-center ml-1">
                          {['', 'Sangat Buruk', 'Buruk', 'Cukup', 'Baik', 'Sangat Baik'][checkoutForm[key]]}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Alasan Keluar *</Label>
                <Textarea
                  id="reason"
                  value={checkoutForm.reason}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, reason: e.target.value })}
                  placeholder="Contoh: Pindah kota, kontrak selesai, dll."
                  className="glass-input border-2 focus:border-isk-teal rounded-xl min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="testimoni">Testimoni</Label>
                <Textarea
                  id="testimoni"
                  value={checkoutForm.testimoni}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, testimoni: e.target.value })}
                  placeholder="Ceritakan pengalaman tinggal Anda... (opsional)"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl min-h-[80px]"
                />
              </div>

              {/* Data Rekening untuk Pengembalian Deposit */}
              <div className="space-y-3 p-4 rounded-xl bg-white/40 border border-white/30">
                <p className="text-sm font-semibold text-isk-text">Rekening Pengembalian Deposit</p>
                <p className="text-xs text-isk-muted">Untuk keperluan pengembalian uang deposit (opsional)</p>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Nama Bank</Label>
                    <select
                      value={checkoutForm.bank_name}
                      onChange={e => setCheckoutForm({ ...checkoutForm, bank_name: e.target.value })}
                      className="w-full glass-input border-2 focus:border-isk-teal rounded-xl px-3 py-2 text-sm text-isk-text focus:outline-none"
                    >
                      <option value="">-- Pilih bank --</option>
                      <option value="BCA">BCA</option>
                      <option value="GOPAY">GOPAY</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nomor Rekening</Label>
                    <Input
                      value={checkoutForm.bank_account}
                      onChange={e => setCheckoutForm({ ...checkoutForm, bank_account: e.target.value })}
                      placeholder="Nomor rekening"
                      className="glass-input border-2 focus:border-isk-teal rounded-xl"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nama Penerima</Label>
                    <Input
                      value={checkoutForm.bank_holder}
                      onChange={e => setCheckoutForm({ ...checkoutForm, bank_holder: e.target.value })}
                      placeholder="Nama sesuai rekening"
                      className="glass-input border-2 focus:border-isk-teal rounded-xl"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowCheckoutModal(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full font-semibold"
                >
                  Ajukan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Complaint Modal */}
      {showComplaintModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Buat Komplain</h3>
              <button onClick={() => setShowComplaintModal(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleComplaintSubmit} className="space-y-4" noValidate>
              <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20">
                <p className="text-sm text-isk-muted">Properti</p>
                <p className="font-semibold text-isk-text">{dashboard?.property_name} - Kamar {dashboard?.room_number}</p>
              </div>

              <div className="space-y-2">
                <Label>Kategori *</Label>
                <Select value={complaintForm.category} onValueChange={(v) => setComplaintForm({ ...complaintForm, category: v })}>
                  <SelectTrigger className="rounded-xl border-2">
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLAINT_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Deskripsi Masalah *</Label>
                <Textarea
                  value={complaintForm.description}
                  onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
                  placeholder="Jelaskan masalah yang Anda alami..."
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Lokasi Spesifik <span className="text-isk-muted text-xs">(jika di area umum)</span></Label>
                <Input
                  type="text"
                  placeholder="Contoh: Lobby, Tangga Lt.2, Dapur Bersama, Koridor"
                  value={complaintForm.location_detail}
                  onChange={(e) => setComplaintForm({ ...complaintForm, location_detail: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>Upload Foto</Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setComplaintForm({ ...complaintForm, file: e.target.files?.[0] || null })}
                  className="hidden"
                  id="complaint-photo-tenant"
                />
                <label
                  htmlFor="complaint-photo-tenant"
                  className="flex items-center justify-center w-full h-24 border-2 border-dashed border-isk-teal/30 rounded-xl cursor-pointer hover:bg-isk-teal/5"
                >
                  {complaintForm.file ? (
                    <div className="text-center">
                      <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
                      <p className="text-sm text-isk-teal">{complaintForm.file.name}</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-isk-muted mx-auto mb-1" />
                      <p className="text-xs text-isk-muted">Klik untuk upload foto</p>
                    </div>
                  )}
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tanggal Preferensi</Label>
                  <Input
                    type="date"
                    value={complaintForm.preferred_date}
                    onChange={(e) => setComplaintForm({ ...complaintForm, preferred_date: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Jam Preferensi (HH:MM)</Label>
                  <Input
                    type="text"
                    placeholder="10:30"
                    value={complaintForm.preferred_time}
                    onChange={(e) => setComplaintForm({ ...complaintForm, preferred_time: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowComplaintModal(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                >
                  Kirim Komplain
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDashboard;
